// web_components/wcVoucherConflictModal/wcVoucherConflictModal.ts

import { WebComponent_Base } from '../../WebComponent_Base';
import { WebComponent_Utils } from '../../WebComponent_Utils';
import { addEventListener, removeEventListener } from '../../../utils/events';
import { renderProductHTML } from './wcVoucherConflictModal_Renderer';
import { applyFocusTrap } from '../../../utils/focusTrap';

/**
 * A custom web component for displaying a voucher conflict modal.
 */
export class WcVoucherConflictModal extends WebComponent_Base {
	public dataAttributes: any;
	public instanceId: string;
	private lastFocusedElement: Element | null = null;
	private handleKeydown!: (event: Event) => void;

	constructor() {
		super();
		this.instanceId = WebComponent_Utils.generateUniqueId();
		this.dataAttributes = {};
		this.handleKeydown = this.handleKeydownEvent.bind(this);
	}

	/**
	 * Gets the name of the component.
	 *
	 * This getter overrides the base class implementation to return the name
	 * of the component. This value is used for logging and
	 * identifying the component in error messages or other diagnostics.
	 *
	 * @returns {string} The name of the component`.
	 */
	protected get componentName(): string {
		return 'WcVoucherConflictModal';
	}

	/**
	 * Retrieves data attributes.
	 * Called from the BaseComponent after stylesheets are attached.
	 */
	protected getComponentData(): void {
		// The condition below handles the voucher data when the modal triggers on a non-single-flow checkout page
		if (window.voucherConflict) this.handleVoucherData();
		this.getData();
	}

	/**
	 * Lifecycle hook called when the component is connected to the DOM.
	 */
	protected onConnected(): void {}

	/**
	 * Lifecycle hook called when the component is removed from the DOM.
	 */
	protected onDisconnected(): void {}

	/**
	 * Grab voucher values (voucher code, discount amount) from the backend and display in the modal.
	 * Applies to Voucher Conflict instances outside the single-flow checkout, which grabs values from the customer JSON data object.
	 */
	protected handleVoucherData(): void {
		const discount = parseFloat(window.vcDiscount);
		const vcDiscountFormatted =
			window.vcDiscountType === 'amount_off'
				? '£' + discount.toFixed(2)
				: discount.toFixed(0) + '%';
		const variables = {
			voucherAmount: vcDiscountFormatted,
			voucherCode: window.vcCode,
			voucherType: window.vcDiscountType,
			voucherMessage: window.vcMessage,
		};

		this.setAttribute('data-variables', JSON.stringify(variables));
	}

	/**
	 * Transfers data attributes from the light DOM elements to this.dataAttributes.
	 * This includes data-css for styling, store ids, product type, variables etc.
	 */
	private getData(): any {
		// Variables
		const variables = this.getAttribute('data-variables');
		if (variables) {
			try {
				this.dataAttributes['variables'] = JSON.parse(variables);
			} catch (e) {
				console.error(
					'wcVoucherConflictModal Component: Invalid JSON in data-variables attribute:',
					e,
				);
				this.dataAttributes['variables'] = null;
			}
		}

		// CSS
		const css = this.getAttribute('data-css');
		if (css) {
			try {
				this.dataAttributes['css'] = JSON.parse(css);
			} catch (e) {
				console.error(
					'wcVoucherConflictModal Component: Invalid CSS in data-css attribute:',
					e,
				);
				this.dataAttributes['css'] = null;
			}
		}
	}

	/**
	 * Renders the component based on the provided `dataAttributes` and `instanceId`.
	 * Appends the generated product content directly to the shadow DOM, after the attached styles.
	 *
	 * @protected
	 * @returns {HTMLElement | null} A reference to the first child of the appended content in the shadow DOM,
	 *                               or `null` if no content was appended.
	 */
	protected render(): HTMLElement | null {
		// Generate the content as a string
		const renderedHTML: string = renderProductHTML(this.dataAttributes, this.instanceId);

		// Convert the string to a DOM node
		const template = document.createElement('template');
		template.innerHTML = renderedHTML;

		// Append the content from the template to the shadow DOM
		this.shadowRoot!.appendChild(template.content.cloneNode(true));

		// Return the reference to the first child of the appended content
		return this.shadowRoot!.lastElementChild as HTMLElement | null;
	}

	/**
	 * Adds event listeners for the buttons and close icon.
	 * Ensures that actions such as accepting cookies or managing cookies are handled.
	 */
	protected attachListeners(): void {
		// Add the ready event listener to apply the modal active properties
		if (document.readyState === 'loading') {
			addEventListener(document, 'DOMContentLoaded', this.applyModalActiveProperties);
		} else {
			this.applyModalActiveProperties();
		}

		const voucherActionElem = this.shadowRoot!.querySelectorAll<HTMLElement>('[data-action]');

		voucherActionElem?.forEach((el) => {
			el?.addEventListener('click', async (e) => {
				const action = el.getAttribute('data-action') as string;

				if (el.tagName === 'A') e.preventDefault();

				if (document.body.classList.contains('checkout')) {
					const base = window.serverData?.contextPath || '';
					const header = document
						.querySelector<HTMLMetaElement>('meta[name="_csrf_header"]')
						?.getAttribute('content');
					const token = document
						.querySelector<HTMLMetaElement>('meta[name="_csrf"]')
						?.getAttribute('content');
					if (!header || !token) return;

					try {
						const response = await fetch(`${base}/ajax/voucher/conflict`, {
							method: 'POST',
							headers: {
								'Content-Type': 'application/x-www-form-urlencoded',
								[header]: token,
							},
							body: new URLSearchParams({ action: action }),
						});

						if (!response.ok) throw new Error(`HTTP error: ${response.status}`);

						window.location.href = document.body.classList.contains('checkout')
							? `${base}/checkout/start`
							: '/';
					} catch (error) {
						console.error('Failed to update voucher', error);
					}
				} else {
					const voucherCodeInput = document.getElementById(
						'voucherCode',
					) as HTMLInputElement;
					const replaceVoucherInput = document.getElementById(
						'replaceVoucher',
					) as HTMLInputElement;
					const voucherCodeSubmitInput = document.getElementById(
						'voucherCodeSubmit',
					) as HTMLInputElement;
					const hiddenVoucherForm = document.getElementById(
						'tt-2-voucher-redemption-form',
					) as HTMLFormElement;
					if (
						!voucherCodeInput &&
						!replaceVoucherInput &&
						!voucherCodeSubmitInput &&
						!hiddenVoucherForm
					)
						return;

					const voucherCodeInputVal = voucherCodeInput.value;
					replaceVoucherInput.value = action == 'replace' ? 'true' : 'false';
					voucherCodeSubmitInput.setAttribute('value', voucherCodeInputVal);
					hiddenVoucherForm.submit();
				}
			});
		});
	}

	/**
	 * Applies the necessary classes to the body and modal.
	 * We are applying a class to both body and the modal itself, rather than just the body,
	 * since we need to adjust the body itself outside the shadowDOM, while also initialising
	 * the modal held in the shadowDOM.
	 */
	applyModalActiveProperties() {
		const body = document.querySelector('body') as HTMLElement;
		const modal = this.shadowRoot!.querySelector('.wc-voucher-conflict-modal') as HTMLElement;
		if (!body && !modal) return;

		setTimeout(function () {
			body.classList.add('modal-active');
			modal?.classList.add('modal-active', 'modal-transition');
		}, 500);

		modal.addEventListener(
			'transitionend',
			() => {
				// Shouldn't need a timeout, but I think there was an issue with the web component
				// loading with the transition simultaneously and this transitionend firing prematurely
				setTimeout(function () {
					modal?.classList.remove('modal-transition');
				}, 500);
			},
			{ once: true },
		);
	}

	/**
	 * Handles the 'keydown' event to listen for the "Escape" key.
	 * When the "Escape" key is pressed, it invokes the acceptAllCookies function.
	 *
	 * @param {Event} event - The event object, cast as a KeyboardEvent to access key properties.
	 */
	handleKeydownEvent(event: Event) {
		if ((event as KeyboardEvent).key === 'Escape') {
			event.stopPropagation();
			this.closeModal();
		}
	}

	/**
	 * Closes the modal and restores focus to the last focused element.
	 * This method ensures that when the modal is dismissed, focus is returned to the
	 * previously focused element to maintain a logical focus flow.
	 * Also removes the keydown listener to prevent memory leaks.
	 */
	closeModal() {
		this.remove();
		if (this.lastFocusedElement) {
			(this.lastFocusedElement as HTMLElement).focus();
		}

		// Remove the keydown event listener when the modal is closed
		removeEventListener(document, 'keydown', this.handleKeydown);
	}

	/**
	 * Manages focus within the modal to ensure accessibility.
	 * Uses `requestAnimationFrame` to delay focus setting until after the browser has had a chance
	 * to render any updates, preventing potential layout thrashing.
	 */
	manageFocus() {
		this.lastFocusedElement = document.activeElement;
		const modal = this.shadowRoot!.getElementById('wc-voucher-conflict-modal') as HTMLElement;
		if (modal) {
			//modal.focus(); // Set focus to the modal when it appears
			applyFocusTrap(modal); // Use the utility function to apply focus trapping
		}
	}

	/**
	 * Applies visibility settings to all elements within the shadow DOM based content.
	 * This ensures elements are shown or hidden according to the presence of variable data
	 * and the component settings file.
	 */
	protected conditionalVisibility(): void {
		WebComponent_Utils.applyVisibilitySettings(<ShadowRoot>this.shadowRoot);
	}
}

/**
 * Registers the custom element:
 * - Checks if the component has already been defined to avoid registration errors
 * - Registers the component with the browser’s CustomElementRegistry
 * - Automatically re-registers the component if the page is restored from Safari’s back-forward cache (bfcache)
 */
WebComponent_Utils.defineComponent('wc-voucher-conflict-modal', WcVoucherConflictModal);
